import { MeetingProvider,useMeeting, useParticipant } from "@videosdk.live/react-sdk";
import {useEffect, useState} from "react";
import { MeetingAppProvider } from "./components/azure-video/MeetingAppContextDef";
import { MeetingContainer } from "./components/azure-video/meeting/MeetingContainer";
import { AblyProvider, ChannelProvider, useChannel, useConnectionStateListener } from 'ably/react';
import { toast } from 'react-toastify';
import { MeasurementPanel, useMeasureCommand, getLatestMeasureState, refreshCaptures } from "./components/MeasurementPanel";
import { LocationPanel } from "./components/LocationPanel";

// Agent's measurement buttons. Rendered inside <MeetingProvider> so the commands can go
// over the call's PubSub (web customer app) as well as Ably (native APK).
function AgentMeasureBar({ channel, roomId }) {
  const send = useMeasureCommand(channel);
  const meeting = useMeeting();
  const [isScreenshotLoading, setIsScreenshotLoading] = useState(false);

  // Screenshot of the customer's camera only. Reads the remote participant's webcam track
  // directly, so it never falls back to the agent's own preview when the customer's camera
  // is off (e.g. while their phone is busy with AR).
  const handleTakeScreenshot = async () => {
    setIsScreenshotLoading(true);
    try {
      const track = remoteCameraTrack(meeting);
      if (!track) {
        const st = getLatestMeasureState();
        if (st && st.active) {
          // Camera is taken by the measuring session: ask the phone to save its own view.
          send("capture");
          toast.info("Customer is measuring, so their camera is busy. Asked their phone to save a capture; it will appear under Captures.", { position: toast.POSITION.BOTTOM_RIGHT });
        } else {
          toast.error("Customer's camera is off. Ask them to turn it on and try again.", { position: toast.POSITION.BOTTOM_RIGHT });
        }
        return;
      }

      const base64Image = await grabJpeg(track);
      const response = await fetch(`${API_BASE}/public/upload-screenshot`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          "image-base64": base64Image,
          "image-name": `screenshot_${roomId}_${Date.now()}.jpg`,
          "room_id": roomId,
        }),
      });
      const result = await response.json().catch(() => ({}));
      if (response.ok && result.status) {
        toast.success("Screenshot uploaded successfully!", { position: toast.POSITION.BOTTOM_RIGHT });
        refreshCaptures();
      } else {
        toast.error("Failed to upload screenshot: " + (result.message || `HTTP ${response.status}`), { position: toast.POSITION.BOTTOM_RIGHT });
      }
    } catch (error) {
      console.error("Screenshot error:", error);
      toast.error("Error capturing screenshot: " + error.message, { position: toast.POSITION.BOTTOM_RIGHT });
    } finally {
      setIsScreenshotLoading(false);
    }
  };

  return (
    <div className="agent-measure-bar">
      <button
        type="button"
        title="Shows a 'Start measuring' card on the customer's phone (web app)"
        onClick={() => {
          send("start_measure", { webOnly: true });
          toast.info("Asked the customer to start measuring.", { position: toast.POSITION.BOTTOM_RIGHT });
        }}
      >
        Ask to Measure
      </button>
      <button type="button" onClick={() => send("Add_corner")}>Add Corner</button>
      <button type="button" onClick={() => send("undo")}>Undo</button>
      <button type="button" onClick={() => send("add_room")}>Add Room</button>
      <button type="button" onClick={() => send("finish_measurement")}>Finish measurement</button>
      <button type="button" disabled={isScreenshotLoading} onClick={handleTakeScreenshot}>
        {isScreenshotLoading ? "Uploading..." : "Take Screenshot"}
      </button>
    </div>
  );
}

const API_BASE = "https://dev.api.customer-connect.satsure.co/api/v1";
// The API rejects request bodies over ~1 MB (nginx 413), so keep the base64 image below this.
const MAX_IMAGE_CHARS = 900000;

// First live webcam track of a participant other than the agent.
function remoteCameraTrack(meeting) {
  const localId = meeting.localParticipant && meeting.localParticipant.id;
  for (const [id, p] of meeting.participants || []) {
    if (id === localId || !p.streams) continue;
    for (const s of p.streams.values()) {
      if (s.kind === "video" && s.track && s.track.readyState === "live") return s.track;
    }
  }
  return null;
}

async function grabJpeg(track) {
  const video = document.createElement("video");
  video.muted = true;
  video.playsInline = true;
  video.srcObject = new MediaStream([track]);
  try {
    await video.play();
    for (let i = 0; i < 30 && !video.videoWidth; i++) await new Promise((r) => setTimeout(r, 100));
    if (!video.videoWidth) throw new Error("customer video has no frames yet, try again");
    let w = video.videoWidth, h = video.videoHeight, quality = 0.9, data = "";
    const canvas = document.createElement("canvas");
    for (let attempt = 0; attempt < 6; attempt++) {
      canvas.width = w; canvas.height = h;
      canvas.getContext("2d").drawImage(video, 0, 0, w, h);
      data = canvas.toDataURL("image/jpeg", quality);
      if (data.length <= MAX_IMAGE_CHARS) return data;
      if (quality > 0.7) quality -= 0.1; else { w = Math.round(w * 0.8); h = Math.round(h * 0.8); }
    }
    return data;
  } finally {
    video.pause();
    video.srcObject = null;
  }
}

export function VideoCallScreen({ roomId,apiresponse }) {
  const [token, setToken] = useState("");
  const [meetingId, setMeetingId] = useState("");
  const [participantName, setParticipantName] = useState("");
  const [micOn, setMicOn] = useState(true);
  const [webcamOn, setWebcamOn] = useState(true);
  const [customAudioStream, setCustomAudioStream] = useState(null);
  const [customVideoStream, setCustomVideoStream] = useState(null)
  const [isMeetingStarted, setMeetingStarted] = useState(false);
  const [isMeetingLeft, setIsMeetingLeft] = useState(false);
  const [capture, setCapture] = useState(false);
  const [isJoined, setIsJoined] = useState(true);

  const {data } = apiresponse || {};

  useConnectionStateListener("connected", () => {
    console.log("Connected to Ably!");
  });

    const { channel } = useChannel("get-started", "first", (message) => {
      // if(message.data === "Start"){
      //   setScreenSharing(true);
      // }else if(message.data === "Stop"){
      //   setScreenSharing(false);
      // }
    });

  return (
    <main className="screen videocall-screen">
      <section className="video-stage">
        {/* <h2>Video Call</h2>
        {roomId ? <p>Room ID: {roomId}</p> : <p>Room ID not provided</p>} */}
        <div style={{width:'100%', height:'100%'}}>
        <MeetingAppProvider>

            <MeetingProvider
              config={{
                meetingId:data[0].video_call_room_id,
                micEnabled: micOn,
                webcamEnabled: webcamOn,
                name: data[0].room_id,
                multiStream: true,
                // customCameraVideoTrack: customVideoStream,
                // customMicrophoneAudioTrack: customAudioStream,      
                // participantId:"srm9-rkhz-9qwc",
              }}
              token={
                data[0].token
              }
              reinitialiseMeetingOnConfigChange={true}
              joinWithoutUserInteraction={true}
            >
              <MeetingContainer
                onMeetingLeave={() => {
                  setToken("");
                  setMeetingId("");
                  setParticipantName("");
                  setWebcamOn(false);
                  setMicOn(false);
                  setMeetingStarted(false);
                }}
                onMeetingJoined={() => {
                  setWebcamOn(true);
                  setMicOn(true);
                  setMeetingStarted(true);
                  setIsJoined(true);
                }}
                setIsMeetingLeft={setIsMeetingLeft}
                isCapture={capture}
                imageCallback={()=>{}}
              />
              {/* Live room measurement from the customer's phone + captures gallery */}
              <MeasurementPanel roomId={roomId} />
              <LocationPanel />
              {isJoined && (
                <AgentMeasureBar channel={channel} roomId={roomId} />
              )}
            </MeetingProvider>
        </MeetingAppProvider>
        </div>

      </section>
    </main>
  )
}